import type { ParsedUrlQuery } from 'querystring';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'urql';
import { createUrqlServerClient } from '@packages/gql';
import { graphql } from '@packages/gql/generated/shopping';
import type { ProductDataQuery } from '@packages/gql/generated/shopping/graphql';
import { useIntl } from 'react-intl';
import config, { getFullLocale } from '@packages/config';
import { promotionBannerDataFetcher } from '@packages/modules/src/PromotionBanner/utils/promotionBannerDataFetcher';
import {
  CompactProduct,
  CustomerRatings,
  ExtendedBreadcrumbs,
  HydrateAtoms,
  ProductPageHead,
  SeoList,
  ProductVariation,
  getProductAndFallbackId,
} from '@packages/product';
import { ErrorBoundary, DynamicYield, Grid } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { Provider as JotaiProvider } from 'jotai';
import { getMainAudience } from '@packages/cms-components';
import { getEcState, logger, useConfig } from '@packages/utilities';
import { ChatBot, Survey } from '@packages/modules';

export const ProductQuery = graphql(/* GraphQL */ `
  query ProductData(
    $categoryId: ID!
    $skipBreadcrumb: Boolean! = false
    $skipCliplister: Boolean! = false
    $skipAplus: Boolean! = false
    $locale: String!
    $lang: String!
    $productId: String!
    $fallbackId: String!
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      akl
      sku
      slug
      defaultCategory
      name
      seo(locale: $locale) {
        breadcrumb
      }
      # ean is not used by any component but we need it for loadbee.
      # If ean is removed loadbee returns null. Maybe mercurius fixes this issue, then ean can be removed again.
      ean
      slug
      ...ProductVariationFragment
      ...ProductSeo
      ...CompactProductFragment
      seoRating(locale: $locale) {
        ...RatingSeoResultFragment
      }
      ...SeoFragment
    }
    breadcrumb(category: $categoryId, locale: $locale) @skip(if: $skipBreadcrumb) {
      ...BreadcrumbsFragment
      ...TrackingBreadcrumbsFragment
    }
  }
`);

export const BreadcrumbQuery = graphql(/* GraphQL */ `
  query BreadcrumbData($categoryId: ID!, $locale: String!) {
    breadcrumb(category: $categoryId, locale: $locale) {
      ...BreadcrumbsFragment
      ...TrackingBreadcrumbsFragment
    }
  }
`);

const getDVOrigin = (query: ParsedUrlQuery) => ({
  searchOrigin: query['nav-q'] ? (query['nav-q'] as string) : '',
  categoryOrigin: query['nav-c'] ? (query['nav-c'] as string) : '',
});

const Detailview = () => {
  const { query, locale: language, defaultLocale: defaultLanguage, replace } = useRouter();
  const { akl, slug, sku, ...otherQueries } = query;

  const { searchOrigin, categoryOrigin } = getDVOrigin(query);
  const { locale } = useIntl();
  const {
    dynamicYieldEnabled,
    product: {
      aplus: { enabled: aplusEnabled },
      cliplister: { enabled: cliplisterEnabled },
    },
  } = useConfig();

  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    if (categoryOrigin && categoryOrigin.length > 0) {
      dispatchGTMEvent({
        categoryId: categoryOrigin,
        categoryIds: [],
        categoryPath: [],
        event: 'categoryData',
        eventCategory: 'data',
      });
    }
  }, [categoryOrigin, dispatchGTMEvent]);

  const context = useMemo(() => ({ suspense: false }), []);

  const [{ data, fetching }] = useQuery({
    query: ProductQuery,
    variables: {
      ...getProductAndFallbackId(akl ?? slug, sku),
      locale: locale!,
      lang: language || defaultLanguage!,
      skipBreadcrumb: categoryOrigin.length === 0,
      categoryId: categoryOrigin,
      skipCliplister: !cliplisterEnabled,
      skipAplus: !aplusEnabled,
    },
    context,
  });
  const { product } = { ...data };

  // if sku in URL differs to the product sku it has to be the default product -> redirect to default sku [NJS-979]
  // has to be client side because of seo needs
  useEffect(() => {
    if (
      (product && !akl) ||
      (product && sku && !Array.isArray(sku) && !product.sku.startsWith(sku))
    ) {
      replace(
        {
          pathname: '/p/[slug]/[akl]',
          query: { ...otherQueries, sku: product.sku, akl: product.akl, slug: product.slug },
        },
        undefined,
        { scroll: false },
      );
    }
  }, [product, sku, akl, replace, otherQueries]);

  let breadcrumb = data?.breadcrumb;

  const [{ data: fallbackBreadcrumb }] = useQuery({
    query: BreadcrumbQuery,
    variables: {
      locale: locale!,
      categoryId: product?.seo?.breadcrumb || product?.defaultCategory || '',
    },
    pause: categoryOrigin.length > 0 || searchOrigin.length > 0,
    context,
  });

  if (!breadcrumb && fallbackBreadcrumb) {
    breadcrumb = fallbackBreadcrumb.breadcrumb;
  }

  if (!product || fetching) return null;

  return (
    <JotaiProvider key={product.akl}>
      <HydrateAtoms>
        <Grid
          container
          rowSpacing={0.5}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <ProductPageHead data={product} />
          {dynamicYieldEnabled && (
            <>
              <DynamicYield dataDyId="dv_notification-campaign" />
              <DynamicYield dataDyId="dv-mkz-banner-popup" />
            </>
          )}
          <Grid item xs={12}>
            <ExtendedBreadcrumbs breadCrumbData={breadcrumb} />
          </Grid>

          <ProductVariation
            searchOrigin={searchOrigin}
            breadcrumbData={breadcrumb}
            productData={product}
            ratingsSlot={
              <Grid item xs={12}>
                {product.seoRating && (
                  <CustomerRatings
                    ratingResultData={product.seoRating}
                    productOverview={
                      <CompactProduct
                        compactProductData={product}
                        withProductName
                        showTaxHint={false}
                      />
                    }
                  />
                )}
              </Grid>
            }
            seoListSlot={
              <Grid item xs={12}>
                <SeoList productData={product} />
              </Grid>
            }
          />

          <ErrorBoundary>
            <Survey />
          </ErrorBoundary>

          <ErrorBoundary>
            <ChatBot />
          </ErrorBoundary>
        </Grid>
      </HydrateAtoms>
    </JotaiProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { res, locale: language, defaultLocale: defaultLanguage, query, req } = context;
  // set response cache-control header (browser cache is unset in development)
  res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { akl, sku, slug } = query;
  const isBot: boolean = req.headers['x-ua-bot'] !== undefined;
  const { categoryOrigin, searchOrigin } = getDVOrigin(query);

  let productData: ProductDataQuery['product'] = null;
  let breadcrumbData: ProductDataQuery['breadcrumb'] = null;
  const locale = getFullLocale(language);

  const { ssrCache, ssrClient } = createUrqlServerClient();
  const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
  const promotionBannerCacheKey = `promotionBannerData-${locale.substring(0, 2)}-${audience}`;

  const [fetchedProductData, promoData] = await Promise.allSettled([
    // get product data
    ssrClient
      .query(ProductQuery, {
        ...getProductAndFallbackId(akl ?? slug, sku),
        locale,
        lang: language || defaultLanguage!,
        skipBreadcrumb: categoryOrigin.length === 0,
        categoryId: categoryOrigin,
        skipCliplister: !config.product.cliplister.enabled,
        skipAplus: !config.product.aplus.enabled,
      })
      .toPromise(),
    promotionBannerDataFetcher(
      { ...query, ...{ shopId: categoryOrigin } },
      req.cookies,
      context,
      locale,
    ),
  ]);

  if (
    fetchedProductData.status === 'rejected' ||
    fetchedProductData.value.error ||
    fetchedProductData.value.data?.product === null
  ) {
    logger.error(
      fetchedProductData.status === 'rejected'
        ? fetchedProductData.reason
        : fetchedProductData.value.error,
      `Error during fetching product (akl: ${akl}, sku: ${sku}, slug: ${slug}), user is bot (${isBot})`,
    );

    if (
      fetchedProductData.status === 'rejected' ||
      fetchedProductData.value.data?.product === null
    ) {
      return {
        notFound: true,
      };
    }
  }

  if (promoData.status === 'rejected') {
    logger.error(
      { description: 'Promotion Banner Data Fetch (Product-Page)' },
      `Could not fetch data for ID ${categoryOrigin}`,
    );
  }

  if (fetchedProductData.status === 'fulfilled' && fetchedProductData.value.data?.product) {
    productData = fetchedProductData.value.data.product;
    breadcrumbData = fetchedProductData.value.data.breadcrumb;
    if (
      !breadcrumbData &&
      productData.defaultCategory &&
      searchOrigin.length === 0 &&
      categoryOrigin.length === 0
    ) {
      await ssrClient
        .query(BreadcrumbQuery, {
          categoryId: productData.seo?.breadcrumb || productData.defaultCategory,
          locale,
        })
        .toPromise();
    }
  }

  return {
    props: {
      urqlState: productData ? ssrCache.extractData() : null,
      fallback: {
        [promotionBannerCacheKey]: promoData.status === 'fulfilled' ? promoData.value : null,
      },
    },
  };
};

// eslint-disable-next-line import/no-default-export
export default Detailview;
